import { useRouter } from 'next/router';
import { Card, Typography } from '../ui';
import { Spinner } from '../../Spinner';
import { PageHeader } from '../PageHeader';
import { useTranslation } from '../../../lib/i18n';
import { useBasePath } from '../../../lib/use-base-path';
import { useWhatsappGroups } from '../../settings/shared/settings-queries';
import { GroupSettingsModal } from '../settings-whatsapp/GroupSettingsModal';
import { connectorsPath } from '../../../lib/connectors-route';
import styles from './WhatsappGroupScreen.module.css';

/**
 * One WhatsApp group at its own address — `channels/whatsapp/group/{id}`.
 *
 * Group settings are a modal now, opened in place from the Channels list. This route stays because
 * **two live server pushes deeplink straight here** (group ready, group failed —
 * `whatsapp-group-service.ts`); it opens the same settings modal over the Channels tab and closing it
 * returns there. A group that isn't in the list (an id from a push for a group since deleted) is stated
 * plainly rather than redirected.
 */
export function WhatsappGroupScreen({ groupId }: { groupId: string }) {
  const { t } = useTranslation();
  const router = useRouter();
  const basePath = useBasePath();
  const { groups, loading, error } = useWhatsappGroups();

  const group = groups.find((g) => g.id === groupId);
  const backPath = `${basePath}${connectorsPath({ tab: 'channels', target: 'whatsapp' })}`;
  const back = () => void router.push(backPath);

  return (
    <div className={styles.screen}>
      <PageHeader
        breadcrumb={[
          { label: t('connectors.title'), href: connectorsPath({ tab: 'channels', target: 'whatsapp' }) },
          { label: group?.subject ?? t('settings.whatsapp.group.settings') },
        ]}
        title={group?.subject ?? t('settings.whatsapp.group.settings')}
      />

      {loading && !group ? (
        <Card className={styles.state}>
          <Spinner />
        </Card>
      ) : group ? (
        <GroupSettingsModal group={group} onClose={back} />
      ) : (
        <Card className={styles.state}>
          <Typography variant="p2" color="secondary">
            {t(error ? 'settings.whatsapp.error' : 'settings.whatsapp.group.missing')}
          </Typography>
        </Card>
      )}
    </div>
  );
}
